import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ADMIN_AUTH_CONFIG_ID, APP_AUTH_CONFIG_ID } from '../admin-auth.constants';
import { USE_ADMIN_TOKEN } from '../admin-http.context';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const oidcSecurityService = inject(OidcSecurityService);

    // Skip interceptor for requests outside the API and workflow engine bases.
    if (!shouldInterceptRequest(req)) {
        return next(req);
    }

    const authConfigId = req.context.get(USE_ADMIN_TOKEN) ? ADMIN_AUTH_CONFIG_ID : APP_AUTH_CONFIG_ID;

    // Get the access token from OidcSecurityService
    return oidcSecurityService.getAccessToken(authConfigId).pipe(
        switchMap((token) => {
            let authReq = req;

            // If we have a token, add it to the Authorization header
            if (token) {
                authReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }

            return next(authReq);
        })
    );
};

/**
 * Determines whether the request should be intercepted for authentication
 */
function shouldInterceptRequest(req: HttpRequest<unknown>): boolean {
    const parsedUrl = parseUrl(req.url);
    if (parsedUrl?.pathname.includes('/api/public/')) {
        return false;
    }

    // Intercept requests to main API or workflow engine (both need Keycloak token)
    return isSameBaseUrl(req.url, environment.apiUrl) || isSameBaseUrl(req.url, environment.workflowEngineUrl);
}

function isSameBaseUrl(url: string, baseUrl: string | undefined): boolean {
    if (!baseUrl) {
        return false;
    }

    const parsedUrl = parseUrl(url);
    const parsedBaseUrl = parseUrl(baseUrl);

    if (!parsedUrl || !parsedBaseUrl || parsedUrl.origin !== parsedBaseUrl.origin) {
        return false;
    }

    const requestPath = normalizePath(parsedUrl.pathname);
    const basePath = normalizePath(parsedBaseUrl.pathname);

    return basePath === '/' || requestPath === basePath || requestPath.startsWith(`${basePath}/`);
}

function parseUrl(url: string): URL | null {
    try {
        return new URL(url, getFallbackOrigin());
    } catch {
        return null;
    }
}

function getFallbackOrigin(): string {
    if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
    }

    return 'http://localhost';
}

function normalizePath(path: string): string {
    const normalizedPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;

    return normalizedPath || '/';
}
